import { Flex, Segmented, Tag, Typography } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import { useCursorList } from '../../lib/use-cursor-list';
import { formatDate, orDash } from '../../lib/format';
import { listDeliveries, type Delivery, type DeliveryStatus } from './api';

const { Text } = Typography;

type View = 'open' | DeliveryStatus;

export function DeliveryListPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [view, setView] = useState<View>('open');

  const { rows, isFetching, hasMore, loadMore } = useCursorList(['deliveries', view, search], (page) =>
    listDeliveries({
      ...page,
      q: search,
      // "Open" is everything not yet handed over. The API filters one status
      // at a time, so SCHEDULED is the one worth defaulting to — a delivery
      // that is READY is waiting on the customer, not on NKS.
      ...(view === 'open' ? { status: 'SCHEDULED' as const } : { status: view }),
    }),
  );

  return (
    <Flex vertical gap={12}>
      <Segmented<View>
        value={view}
        onChange={(value) => {
          setView(value);
        }}
        size="large"
        block
        options={[
          { value: 'open', label: t('delivery.viewPreparing') },
          { value: 'READY', label: t('delivery.viewReady') },
          { value: 'DELIVERED', label: t('delivery.viewDelivered') },
        ]}
      />

      <ListPage<Delivery>
        title={t('delivery.title')}
        searchPlaceholder={t('delivery.search')}
        onSearch={(value) => {
          setSearch(value);
        }}
        loading={isFetching}
        emptyDescription={search.length > 0 ? t('delivery.noResults') : t('delivery.empty')}
        columns={[
          {
            title: t('delivery.fields.doNo'),
            dataIndex: 'doNo',
            width: 175,
            render: (value: string) => <span className="tabular">{value}</span>,
          },
          { title: t('sales.fields.customer'), dataIndex: 'customerName' },
          {
            title: t('vehicles.fields.chassisNo'),
            dataIndex: 'chassisNo',
            width: 190,
            render: (value: string) => <Text className="tabular">{value}</Text>,
          },
          {
            title: t('delivery.fields.inspection'),
            width: 160,
            render: (_: unknown, row: Delivery) =>
              row.pdiOutstanding.length === 0 ? (
                <Tag color={row.pdiFailed.length > 0 ? 'warning' : 'success'}>
                  {row.pdiFailed.length > 0
                    ? t('delivery.failedCount', { count: row.pdiFailed.length })
                    : t('delivery.allPassed')}
                </Tag>
              ) : (
                <Text type="secondary">
                  {t('delivery.progress', {
                    done: row.pdiItems.length - row.pdiOutstanding.length,
                    total: row.pdiItems.length,
                  })}
                </Text>
              ),
          },
          {
            title: t('delivery.fields.scheduled'),
            dataIndex: 'scheduledAt',
            width: 150,
            render: (value: string | null) => (value === null ? t('delivery.noDate') : formatDate(value)),
          },
          {
            title: t('delivery.fields.delivered'),
            dataIndex: 'deliveredAt',
            width: 150,
            render: (value: string | null) => (value === null ? '—' : formatDate(value)),
          },
        ]}
        rows={rows}
        hasMore={hasMore}
        onLoadMore={loadMore}
        rowKey={(row) => row.uid}
        onRowClick={(row) => {
          void navigate(`/deliveries/${row.uid}`);
        }}
        renderMobileRow={(row) => (
          <Flex vertical gap={4}>
            <Flex justify="space-between" align="center" gap={8}>
              <Text strong>{row.customerName}</Text>
              <Tag color={row.status === 'DELIVERED' ? 'success' : undefined} style={{ marginInlineEnd: 0 }}>
                {t(`delivery.status.${row.status}`)}
              </Tag>
            </Flex>
            <Flex justify="space-between" gap={8}>
              <Text type="secondary" className="tabular" style={{ fontSize: 12 }}>
                {row.chassisNo}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {orDash(row.model)}
              </Text>
            </Flex>
          </Flex>
        )}
      />
    </Flex>
  );
}
